import { useMemo, useRef, useState } from 'react'
import { DeviceEventEmitter } from 'react-native';

import { message } from 'antd'
import { useRouter } from 'next/router';

import { favoriteAdd, favoriteDelete } from '@api/index'
import { SpotTradeAPI } from '@api/spotTrade';
import { PRECISION } from '@constants/index';
import useAuthSession from '@lib/hooks/useAuthSession'
import { useIsomorphicLayoutEffect } from '@lib/hooks/useIsomorphicLayoutEffect';
import { getLoginPath, I18n } from '@utils/index'

import { useCoinDetail } from '../KLineHeader/useCoinDetail'

let fromSymbol = ''

export const useMain = (params) => {
  const { marketStore, coinCode, tradeStore, updateLoadingState } = params;
  const [asks, setAsks] = useState([]);
  const [bids, setBids] = useState([]);
  const [midPrice, setMidPrice] = useState('--');
  const [midPriceRise, setMidPriceRise] = useState('--');
  const midPriceRef = useRef('--');
  const subscription = useRef(null);
  const router = useRouter()
  const isLogin = useAuthSession({ redirectTo: null })
  const coinArr = coinCode?.split?.('_');
  const coinDetail = useCoinDetail({ coinCode: coinArr?.[0] })
  const initFetch = async () => {
    await Promise.allSettled([marketStore.fetchMarketSymbol(), marketStore.fetchMarketCoin(), getOrderbooks()])
    updateLoadingState({ mainStatus: 'success' })
    onWebSocketReady()
    subscription.current = DeviceEventEmitter.addListener('AsksAndBidsData', (eventData) => {
      eventData.asks.length < 20 && console.log(eventData)
      if (eventData.symbol === coinCode) {
        setAsksAndBidsData(eventData)
      }
    })
  }

  const onWebSocketReady = () => {
    if (coinCode === fromSymbol) return
    DeviceEventEmitter.emit('sendWsData', { symbol: coinCode, room: 'orderbook', fromSymbol: fromSymbol, jwtToken: '' })
    fromSymbol = coinCode
  }

  const getOrderbooks = async () => {
    setAsks([])
    setBids([])
    try {
      const response = await SpotTradeAPI.getOrderbooks({ symbol: coinCode });
      if (response && response.success && response.obj?.bids && response.obj.asks) {
        setAsksAndBidsData(response.obj)
      }
    } catch (error) {
      console.log(error)
    }
    return Promise.resolve()
  };

  const setAsksAndBidsData = (t) => {
    try {
      let sum1 = 0;
      let sum2 = 0;
      t.bids.forEach((item) => {
        sum1 += parseFloat(item.v)
      })
      t.asks.forEach((item) => {
        sum2 += parseFloat(item.v)
      })

      const max = Math.max(sum1, sum2)

      t.bids.forEach((item, index) => {
        t.bids[index].sum = parseFloat(item.v) + parseFloat((t.bids[index - 1] || {}).sum ?? 0)
        t.bids[index].barPercent = (t.bids[index].sum / max)
      })
      t.asks.forEach((item, index) => {
        t.asks[index].sum = parseFloat(item.v) + parseFloat((t.asks[index - 1] || {}).sum ?? 0)
        t.asks[index].barPercent = (t.asks[index].sum / max)
      })
      setAsks(t.asks);
      setBids(t.bids);
    } catch (error) {
      console.log(error)
    }
  };

  useIsomorphicLayoutEffect(() => {
    coinCode && initFetch()
    return () => {
      subscription.current?.remove?.()
    }
  }, [coinCode])

  // 如果用户登录 获取自选盘口 本期不做自选 内容隐藏
  // useIsomorphicLayoutEffect(() => {
  //   isLogin && tradeStore.fetchFavoriteList();
  // }, [isLogin])

  const getCoinInfo = () => {
    const coinDetail = marketStore.marketSymbols.find(item => item.coinCode === coinCode);
    return {
      precision: coinDetail?.symbolPrecision ?? PRECISION,
      coinDetail
    }
  }

  const currentSymbol = getCoinInfo();
  useIsomorphicLayoutEffect(() => {
    const currMidPrice = currentSymbol?.coinDetail?.priceLast;
    const midPriceRise = midPriceRef.current !== '--' ? Number(currMidPrice) - Number(midPriceRef.current) : 0
    setMidPrice(currMidPrice);
    midPriceRef.current = currMidPrice;
    setMidPriceRise(midPriceRise);
  }, [coinCode, currentSymbol?.coinDetail])

  const isFavorite = useMemo(() => {
    const { favoriteObject } = tradeStore;
    const symbols = isLogin ? favoriteObject.symbols : [];
    return !!symbols?.find(item => item.symbol === coinCode)
    // eslint-disable-next-line
  }, [isLogin, coinCode, tradeStore.favoriteObject])

  const favoriteChange = async () => {
    if (!isLogin) {
      router.push(getLoginPath())
      return;
    }
    let res
    if (isFavorite) {
      res = await favoriteDelete({ items: coinCode, favorite_type: 'symbol' })
    } else {
      res = await favoriteAdd({ items: coinCode, favorite_type: 'symbol' })
    }
    if (res && res.success) {
      const success = isFavorite ? I18n.t('remove_favorite_success') : I18n.t('add_favorite_success');
      message.success(I18n.t(success))
    } else {
      const error = isFavorite ? I18n.t('remove_favorite_failed') : I18n.t('add_favorite_failed');
      message.error(error)
      tradeStore.firstShowCustomChoicesChange(false)
    }
    await tradeStore.fetchFavoriteList();
  };

  return {
    coinArr,
    asks,
    bids,
    midPrice,
    midPriceRise,
    isFavorite,
    ...getCoinInfo(),
    onWebSocketReady,
    favoriteChange,
    coinDetail
  }
}
